import {
  createContext,
  type ReactNode,
  type RefObject,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import { useLocation, useNavigate } from "react-router";

/**
 * Back closes what is on top.
 *
 * A layered surface — the camera, the day's photograph, an emoji picker, a
 * report sheet, an album opened from the profile, a day's panel — is a
 * pushed screen to the person holding the phone, and on a phone the way
 * back off a pushed screen is the Back gesture: Android's button or edge
 * swipe, Safari's edge swipe. Both drive the browser's history, and the
 * surfaces lived in React state alone, so Back stepped the address out
 * from under them: the camera stayed open, streaming, over the feed it had
 * never been opened from.
 *
 * Each surface pushes a history entry for the address it is on, with a
 * token of its own in the entry's state, and closes itself when that entry
 * is left. Closed by its own button, it pops the entry it pushed, so the
 * history holds no dead stops. The router owns the history, so the entry
 * goes through the router: a plain `pushState` would leave it counting
 * from an index it never saw.
 *
 * The provider is the app's, not the hook's: the surfaces are rendered in
 * tests without a router, and there the hook does nothing at all.
 */
type Entry = { token: string; path: string };

type SurfaceHistory = {
  /** The surfaces the current entry knows, bottom first. */
  stack: readonly Entry[];
  /**
   * The same, as of the last commit rather than the render a caller saw:
   * a surface unmounted by a navigation elsewhere reads this in its cleanup,
   * after the provider has already moved on, and must find its own token
   * gone from the top.
   */
  latest: RefObject<readonly Entry[]>;
  push: (token: string) => void;
  /** Back by `depth` entries. */
  pop: (depth: number) => void;
};

const SurfaceHistoryContext = createContext<SurfaceHistory | null>(null);

function stackOf(state: unknown): readonly Entry[] {
  const stack = (state as { surfaces?: unknown } | null)?.surfaces;
  return Array.isArray(stack)
    ? stack.filter(
        (entry): entry is Entry =>
          typeof entry === "object" &&
          entry !== null &&
          typeof (entry as Entry).token === "string" &&
          typeof (entry as Entry).path === "string",
      )
    : [];
}

/** How many entries at the top were pushed on this very address. */
function pushedHere(stack: readonly Entry[], path: string): number {
  let depth = 0;
  for (let index = stack.length - 1; index >= 0 && stack[index]?.path === path; index--) depth++;
  return depth;
}

export function SurfaceHistoryProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  // By the entry, not by identity: the state object is new on every render.
  const stack = useMemo(() => stackOf(location.state), [location.state]);
  const latest = useRef(stack);
  useLayoutEffect(() => {
    latest.current = stack;
  }, [stack]);

  /*
   * A fresh document standing on a surface's entry — the page was reloaded
   * with the day's panel open, say. Nothing is open any more; the entries
   * pushed on this address sit on top of the very page now showing, and
   * Back through them changed nothing a person could see. Step down to the
   * page under them, once. Entries a swipe carried here from another
   * address stay: what is under those is a different page.
   */
  const settled = useRef(false);
  useEffect(() => {
    if (settled.current) return;
    settled.current = true;
    const depth = pushedHere(latest.current, location.pathname);
    if (depth > 0) void navigate(-depth);
  }, [navigate, location.pathname]);

  const value = useMemo<SurfaceHistory>(
    () => ({
      stack,
      latest,
      push: (token) => {
        void navigate(location.pathname + location.search + location.hash, {
          state: {
            ...((location.state as object | null) ?? {}),
            surfaces: [...stack, { token, path: location.pathname }],
          },
          // The address is the same; a push to it must not scroll to the top.
          preventScrollReset: true,
        });
      },
      pop: (depth) => {
        void navigate(-depth);
      },
    }),
    [navigate, location, stack],
  );
  return <SurfaceHistoryContext value={value}>{children}</SurfaceHistoryContext>;
}

/** A token nothing else has: not the hook's identity, which a remount repeats. */
function freshToken(): string {
  return Math.random().toString(36).slice(2, 10);
}

/**
 * A surface's history entry: `push` when it opens, `pop` when it closes
 * itself, and `onLeave` when the entry is left another way — Back.
 *
 * A pop takes everything above the entry with it: a day opened inside an
 * album cannot outlive the album, and closing the album by its own button
 * must not leave the day's entry as a dead stop on the way back.
 *
 * Both functions keep their identity for the life of the caller, so a
 * mount-only effect can hold them.
 */
export function useSurfaceEntry(onLeave: () => void): { push: () => void; pop: () => void } {
  const history = useContext(SurfaceHistoryContext);
  const historyRef = useRef(history);
  historyRef.current = history;
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;
  // Minted at each push rather than once per hook: a stale entry — one
  // stepped Forward into after its surface closed — must never match a
  // surface opened later from the same place.
  const token = useRef<string | null>(null);
  // Whether the entry has been seen: before the push lands, the stack does
  // not hold the token either, and that absence is not a Back.
  const seen = useRef(false);
  const held =
    token.current !== null &&
    (history?.stack.some((entry) => entry.token === token.current) ?? false);

  useEffect(() => {
    if (held) {
      seen.current = true;
      return;
    }
    if (seen.current) {
      seen.current = false;
      onLeaveRef.current();
    }
  }, [held]);

  const push = useCallback(() => {
    if (!historyRef.current) return;
    token.current = freshToken();
    historyRef.current.push(token.current);
  }, []);
  const pop = useCallback(() => {
    seen.current = false;
    // Only when the entry is still there: left by Back it is gone already,
    // and a surface unmounted by a navigation elsewhere must not undo it.
    const history = historyRef.current;
    const stack = history?.latest.current ?? [];
    const index = stack.findIndex((entry) => entry.token === token.current);
    token.current = null;
    if (history && index >= 0) history.pop(stack.length - index);
  }, []);
  return { push, pop };
}
